import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, filter, map, Subject, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/exit-modal/exit-modal.constants';
import { SharedModule } from 'src/app/components/shared/shared.module';
import { TableSearchComponent } from 'src/app/components/shared/table/components/table-search-component/table-search.component';
import { SpecialCharacters } from 'src/app/core/enums';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { IMAGE_SRC } from '../../../components/shared/shared-constants';
import { CvisSwap } from '../../core/models/cvis-swap.model';
import { CvisSwapService } from '../../core/services/cvis-swap.service';
import { AddEditSwapComponent } from '../shared/add-edit-swap/add-edit-swap.component';
import { SwapData } from '../shared/add-edit-swap/add-edit-swap.constants';
import {
  ColumnIds,
  ColumnText,
  CONSTANTS,
  EXCEL_HEADERS,
  FILE_NAME,
  REVERSE_SERVICE_CODE_MAP,
  REVERSE_SORT_INDICATOR_MAP,
  SERVICE_CODE_MAP,
  SORT_INDICATOR_MAP,
} from './cvis-swap.constants';

@Component({
  selector: 'app-cvis-swap',
  imports: [TableSearchComponent, FormsModule, SharedModule, MatRadioModule],
  templateUrl: './cvis-swap.component.html',
  styleUrl: './cvis-swap.component.scss',
})
export class CvisSwapComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy = inject(DestroyRef);

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly CONSTANTS = CONSTANTS;
  readonly IMAGES = IMAGE_SRC;
  readonly SPECIAL_CHARS = SpecialCharacters;

  selectedCvis: CvisSwap | null = null;
  dataSource = new MatTableDataSource<CvisSwap>();
  destroy$ = new Subject<void>();

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  dropdownOpen = [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ];
  activeDropDown: Items[] = [];
  origSortDropDown: Items[] = [];
  destSortDropDown: Items[] = [];
  serviceDropDown: Items[] = [];
  swapSortDropDown: Items[] = [];
  swapServiceDropDown: Items[] = [];

  idData = this.commonUtils.createTableColumn(ColumnIds.ID);
  nameData = this.commonUtils.createTableColumn(ColumnIds.NAME);
  activeData = this.commonUtils.createTableColumn(ColumnIds.ACTIVE);
  effectiveData = this.commonUtils.createTableColumn(ColumnIds.EFFECTIVE);
  expiresData = this.commonUtils.createTableColumn(ColumnIds.EXPIRES);
  monData = this.commonUtils.createTableColumn(ColumnIds.MON);
  tueData = this.commonUtils.createTableColumn(ColumnIds.TUE);
  wedData = this.commonUtils.createTableColumn(ColumnIds.WED);
  thuData = this.commonUtils.createTableColumn(ColumnIds.THU);
  friData = this.commonUtils.createTableColumn(ColumnIds.FRI);
  satData = this.commonUtils.createTableColumn(ColumnIds.SAT);
  sunData = this.commonUtils.createTableColumn(ColumnIds.SUN);
  origCodeData = this.commonUtils.createTableColumn(ColumnIds.ORIG_CODE);
  origSortData = this.commonUtils.createTableColumn(ColumnIds.ORIG_SORT);
  destSlicData = this.commonUtils.createTableColumn(ColumnIds.DEST_SLIC);
  destSortData = this.commonUtils.createTableColumn(ColumnIds.DEST_SORT);
  serviceData = this.commonUtils.createTableColumn(ColumnIds.SERVICE);
  swapSlicData = this.commonUtils.createTableColumn(ColumnIds.SWAP_SLIC);
  swapSortData = this.commonUtils.createTableColumn(ColumnIds.SWAP_SORT);
  swapServiceData = this.commonUtils.createTableColumn(ColumnIds.SWAP_SERVICE);
  swapPkgLocRoleData = this.commonUtils.createTableColumn(
    ColumnIds.SWAP_PKG_LOC_ROLE,
  );
  monLcdData = this.commonUtils.createTableColumn(ColumnIds.MON_LCD);
  tueLcdData = this.commonUtils.createTableColumn(ColumnIds.TUE_LCD);
  wedLcdData = this.commonUtils.createTableColumn(ColumnIds.WED_LCD);
  thuLcdData = this.commonUtils.createTableColumn(ColumnIds.THU_LCD);
  friLcdData = this.commonUtils.createTableColumn(ColumnIds.FRI_LCD);
  satLcdData = this.commonUtils.createTableColumn(ColumnIds.SAT_LCD);
  sunLcdData = this.commonUtils.createTableColumn(ColumnIds.SUN_LCD);

  constructor(
    readonly dialog: MatDialog,
    public commonUtils: CommonUtils,
    private readonly cvisSwapService: CvisSwapService,
  ) {}

  ngOnInit(): void {
    this.getCvisSwapConfiguration();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.commonUtils.createFilter();
  }

  getCvisSwapConfiguration() {
    this.cvisSwapService
      .getCvisSwap()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((response) => {
          this.dataSource.data = this.mapCvisSwapData(response);
        }),
      )
      .subscribe();
  }

  mapCvisSwapData(data: any): any[] {
    const transformedData = data.response.listOfCvisSwap.map((item: any) => ({
      ...item,
      active: item.active ? 'Y' : 'N',
      origSort: SORT_INDICATOR_MAP[item.origSort] || item.origSort,
      destSort: SORT_INDICATOR_MAP[item.destSort] || item.destSort,
      swapSort: SORT_INDICATOR_MAP[item.swapSort] || item.swapSort,
      service: SERVICE_CODE_MAP[item.service] || item.service,
      swapService: SERVICE_CODE_MAP[item.swapService] || item.swapService,
    }));

    this.dataSource.data = transformedData;

    this.activeDropDown = this.createDropdown(transformedData, 'active');
    this.origSortDropDown = this.createDropdown(transformedData, 'origSort');
    this.destSortDropDown = this.createDropdown(transformedData, 'destSort');
    this.serviceDropDown = this.createDropdown(transformedData, 'service');
    this.swapSortDropDown = this.createDropdown(transformedData, 'swapSort');
    this.swapServiceDropDown = this.createDropdown(
      transformedData,
      'swapService',
    );

    return transformedData;
  }

  private createDropdown(data: any[], key: string): any[] {
    const uniqueValues = [...new Set(data.map((item) => item[key]))];
    return uniqueValues.map((val) => ({
      id: val,
      selected: true,
    }));
  }

  addCvis() {
    this.dialog
      .open(AddEditSwapComponent, {
        width: '50rem',
        height: '47.5rem',
        disableClose: true,
        data: { edit: false, isEdi: false } as SwapData,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((data) => {
        if (data) {
          this.cvisSwapService
            .addCvisSwap(data)
            .pipe(
              take(1),
              filter((val) => !!val),
            )
            .subscribe(() => {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              setTimeout(() => {
                this.getCvisSwapConfiguration();
              }, 500);
            });
        }
      });
  }

  editCvis() {
    const isString = (val: any): val is string => typeof val === 'string';
    if (this.selectedCvis) {
      const selected = { ...this.selectedCvis };

      if (isString(selected.active)) {
        selected.active = selected.active === 'Y';
      }

      selected.origSort =
        REVERSE_SORT_INDICATOR_MAP[selected.origSort] || selected.origSort;
      selected.destSort =
        REVERSE_SORT_INDICATOR_MAP[selected.destSort] || selected.destSort;
      selected.swapSort =
        REVERSE_SORT_INDICATOR_MAP[selected.swapSort] || selected.swapSort;
      selected.service =
        REVERSE_SERVICE_CODE_MAP[selected.service] || selected.service;
      selected.swapService =
        REVERSE_SERVICE_CODE_MAP[selected.swapService] || selected.swapService;

      const dialogRef = this.dialog.open(AddEditSwapComponent, {
        width: '50rem',
        height: '47.5rem',
        disableClose: true,
        data: { edit: true, isEdi: false, val: selected } as SwapData,
      });
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((data) => {
          if (data) {
            this.cvisSwapService
              .editCvisSwap(data)
              .pipe(
                take(1),
                filter((val) => !!val),
              )
              .subscribe(() => {
                this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                setTimeout(() => {
                  this.getCvisSwapConfiguration();
                }, 500);
              });
          }
        });
    }
  }

  deleteCvis() {
    if (this.selectedCvis?.id) {
      this.commonUtils
        .openWarningModal({
          header: this.CONSTANTS.WARNING_MODAL.HEADER,
          body: this.CONSTANTS.WARNING_MODAL.BODY,
          extraText: this.selectedCvis?.name,
        })
        .afterClosed()
        .subscribe((resp) => {
          if (resp?.button === BUTTON_CLICK.ACCEPT) {
            this.cvisSwapService
              .deleteCvis(this.selectedCvis!.id)
              .pipe(take(1))
              .subscribe((res) => {
                if (res.code === 200) {
                  this.getCvisSwapConfiguration();
                  this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                }
              });
          }
        });
    }
  }

  exportExcel() {
    const filteredData = this.dataSource.filteredData.map((item) => {
      const {
        id,
        active,
        effective,
        expires,
        name,
        mon,
        tue,
        wed,
        thu,
        fri,
        sat,
        sun,
        origCode,
        origSort,
        destSlic,
        destSort,
        service,
        swapSlic,
        swapSort,
        swapService,
        monLcd,
        tueLcd,
        wedLcd,
        thuLcd,
        friLcd,
        satLcd,
        sunLcd,
      } = item;

      const reverseBool = (val: any) => (val === 'Y' ? true : false);

      return {
        id,
        name,
        active: reverseBool(active),
        effective,
        expires,
        mon,
        tue,
        wed,
        thu,
        fri,
        sat,
        sun,
        origCode,
        origSort: REVERSE_SORT_INDICATOR_MAP[origSort] || origSort,
        destSlic,
        destSort: REVERSE_SORT_INDICATOR_MAP[destSort] || destSort,
        service: REVERSE_SERVICE_CODE_MAP[service] || service,
        swapSlic,
        swapSort: REVERSE_SORT_INDICATOR_MAP[swapSort] || swapSort,
        swapService: REVERSE_SERVICE_CODE_MAP[swapService] || swapService,
        monLcd,
        tueLcd,
        wedLcd,
        thuLcd,
        friLcd,
        satLcd,
        sunLcd,
      };
    });

    this.commonUtils.exportExcel(EXCEL_HEADERS, FILE_NAME, filteredData);
  }

  onStatusChangeForDropdowns(
    $event: string | boolean | unknown[],
    field: TableColumn,
  ) {
    field.ids[0] = $event as unknown as Items;
    this.search();
  }

  search() {
    this.commonUtils.searchFilter(
      [
        this.idData,
        this.nameData,
        this.effectiveData,
        this.expiresData,
        this.origCodeData,
        this.destSlicData,
        this.destSortData,
        this.swapSlicData,
        this.swapPkgLocRoleData,
        this.monLcdData,
        this.tueLcdData,
        this.wedLcdData,
        this.thuLcdData,
        this.friLcdData,
        this.satLcdData,
        this.sunLcdData,
      ],
      this.dataSource,
      [
        this.activeData,
        this.monData,
        this.tueData,
        this.wedData,
        this.thuData,
        this.friData,
        this.satData,
        this.sunData,
        this.origSortData,
        this.swapSortData,
        this.destSortData,
        this.swapServiceData,
        this.serviceData,
      ],
    );
  }
}
